'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import NavLink from '@/components/client/navigation/NavLink'
import SignOutIcon from '@/components/icons/SignOutIcon'
import { CLIENT_PAGES, type ClientPagePath } from '@/constants/clientPortalPages'
import { CLIENT_KIT_REGISTRATION_FIRST_STEP_PATH } from '@/components/client/kit-registration/flow'
import StartNewModal, {
  type StartNewModalVariant,
} from '@/components/client/navigation/StartNewModal'
import {
  DROP_SHIPMENT_ORDER_STATE_EVENT,
  START_NEW_DROP_SHIPMENT_ORDER_EVENT,
  START_NEW_KIT_REGISTRATION_EVENT,
  START_NEW_ORDER_EVENT,
  type DropShipmentOrderStateDetail,
} from '@/utilities/orders/events'
import { cn } from '@/utilities/ui'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import {
  Check,
  ChevronDown,
  ClipboardPlus,
  ClipboardList,
  CopyPlus,
  FileText,
  Palette,
  Plus,
  type LucideIcon,
  UserIcon,
  UserRound,
  Users,
} from 'lucide-react'
import { type MouseEventHandler, type ReactNode, useCallback, useEffect, useState } from 'react'
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin'
import { isPathActive } from '@/utilities/navigation'
import { isDashboardNavPath } from '@/utilities/dashboard/dashboardContext'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

type DesktopNavItem = {
  id: 'dashboard' | 'orders' | 'myProfile'
  label: string
  href: string
}

type NavMenuDesktopProps = {
  items: readonly DesktopNavItem[]
  pathname: string
  canPlaceOrder: boolean
  canAccessKitRegistration: boolean
  canAccessDropShipmentOrder: boolean
  canViewFiles?: boolean
  isProfileMenuOpen: boolean
  onProfileMenuOpenChange: (open: boolean) => void
  isProgramScopedNavDisabled?: boolean
  hasSelectedClient?: boolean
  isImpersonating?: boolean
}

const ORDERS_DISABLED_TOOLTIP = 'Select a program to view orders'
const PLACE_ORDER_DISABLED_TOOLTIP = 'Select a program to place an order'
const REGISTER_KIT_DISABLED_TOOLTIP = 'Select a program to register a kit'
const PLACE_DS_ORDER_DISABLED_TOOLTIP = 'Select a program to place a drop shipment order'
const PROFILE_DISABLED_TOOLTIP = 'Select a program to view profile and settings'

const PRIMARY_ACTION_LINK_CLASS = cn(
  'relative inline-flex h-10 items-center gap-2 rounded-full bg-cp-bright-blue px-4 text-sm text-white',
  'hover:bg-cp-bright-blue-hover active:bg-cp-bright-blue-active transition-colors cursor-pointer',
)

const FOCUS_VISIBLE_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-bright-blue'

function DisabledDesktopAction({
  testId,
  label,
  tooltip,
  children,
}: Readonly<{
  testId: string
  label: string
  tooltip: string
  children: ReactNode
}>) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-disabled="true"
            aria-label={`${label}. ${tooltip}`}
            className={cn(
              'relative inline-flex h-10 cursor-not-allowed items-center gap-2 rounded-full border-0',
              'bg-cp-bright-blue/50 px-4 text-sm text-white opacity-70',
              FOCUS_VISIBLE_RING,
            )}
            data-testid={testId}
            data-disabled="true"
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="max-w-xs border-light-grey bg-white text-cp-dark-blue"
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function DesktopProfileItem({
  href,
  label,
  Icon,
  isActive,
  disabled,
  onClick,
  testId,
  className,
}: Readonly<{
  href: string
  label: string
  Icon: LucideIcon
  isActive: boolean
  disabled: boolean
  onClick: () => void
  testId: string
  className?: string
}>) {
  const content = (
    <>
      <Icon
        className={cn('h-5 w-5', disabled ? 'text-cp-placeholder' : 'text-cp-bright-blue')}
        aria-hidden="true"
      />
      <span
        className={cn(
          'text-sm',
          disabled ? 'text-cp-placeholder' : isActive ? 'text-cp-bright-blue' : 'text-cp-dark-blue',
        )}
      >
        {label}
      </span>
    </>
  )

  if (disabled) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex w-full cursor-not-allowed items-center gap-3 rounded-full border-0 bg-transparent px-3 py-3 opacity-60',
                FOCUS_VISIBLE_RING,
                className,
              )}
              aria-disabled="true"
              aria-label={`${label}. ${PROFILE_DISABLED_TOOLTIP}`}
              data-testid={testId}
              data-disabled="true"
            >
              {content}
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="left"
            className="max-w-xs border-light-grey bg-white text-cp-dark-blue"
          >
            {PROFILE_DISABLED_TOOLTIP}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <Link
      href={href}
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 rounded-full px-3 py-3 hover:bg-cp-special-grey',
        isActive && 'bg-cp-special-grey',
        className,
      )}
      aria-current={isActive ? 'page' : undefined}
      onClick={onClick}
      data-testid={testId}
    >
      {content}
    </Link>
  )
}

type DesktopPrimaryActionsProps = {
  pathname: string
  canPlaceOrder: boolean
  canAccessKitRegistration: boolean
  canAccessDropShipmentOrder: boolean
  isProgramScopedNavDisabled: boolean
  isDropShipmentOrderCompleted: boolean
  onCloseMenus: () => void
  onStartNew: (variant: StartNewModalVariant) => void
  onRestartDropShipmentOrder: () => void
}

function getDesktopPrimaryAction({
  isKitRegistrationActive,
  isDropShipmentOrderActive,
  canPlaceOrder,
  canAccessKitRegistration,
  canAccessDropShipmentOrder,
}: {
  isKitRegistrationActive: boolean
  isDropShipmentOrderActive: boolean
  canPlaceOrder: boolean
  canAccessKitRegistration: boolean
  canAccessDropShipmentOrder: boolean
}): { label: string; href: ClientPagePath } {
  if (
    isKitRegistrationActive ||
    (!isDropShipmentOrderActive && !canPlaceOrder && canAccessKitRegistration)
  ) {
    return { label: 'Register Kit', href: CLIENT_KIT_REGISTRATION_FIRST_STEP_PATH }
  }
  if (isDropShipmentOrderActive || (!canPlaceOrder && canAccessDropShipmentOrder)) {
    return { label: 'Place DS Order', href: CLIENT_PAGES.placeDropShipmentOrder }
  }
  return { label: 'Place Order', href: CLIENT_PAGES.placeOrder }
}

function DesktopPrimaryActions({
  pathname,
  canPlaceOrder,
  canAccessKitRegistration,
  canAccessDropShipmentOrder,
  isProgramScopedNavDisabled,
  isDropShipmentOrderCompleted,
  onCloseMenus,
  onStartNew,
  onRestartDropShipmentOrder,
}: Readonly<DesktopPrimaryActionsProps>) {
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false)
  const isDropShipmentOrderActive =
    canAccessDropShipmentOrder && pathname === CLIENT_PAGES.placeDropShipmentOrder
  const isPlaceOrderActive =
    isPathActive(pathname, CLIENT_PAGES.placeOrder) && !isDropShipmentOrderActive
  const isKitRegistrationActive = isPathActive(pathname, CLIENT_PAGES.kitRegistration)
  const canShowEnabledActions = !isProgramScopedNavDisabled
  const enabledPrimaryActionCount =
    Number(canPlaceOrder) + Number(canAccessKitRegistration) + Number(canAccessDropShipmentOrder)
  const showSinglePlaceOrderAction =
    canShowEnabledActions && canPlaceOrder && enabledPrimaryActionCount === 1
  const showSingleKitRegistrationAction =
    canShowEnabledActions && canAccessKitRegistration && enabledPrimaryActionCount === 1
  const showSingleDropShipmentAction =
    canShowEnabledActions && canAccessDropShipmentOrder && enabledPrimaryActionCount === 1
  const showActionsDropdown = canShowEnabledActions && enabledPrimaryActionCount >= 2
  const showDisabledPlaceOrder = isProgramScopedNavDisabled && canPlaceOrder
  const showDisabledKitRegistration = isProgramScopedNavDisabled && canAccessKitRegistration
  const showDisabledDropShipment = isProgramScopedNavDisabled && canAccessDropShipmentOrder
  const { label: activeActionLabel, href: activeActionHref } = getDesktopPrimaryAction({
    isKitRegistrationActive,
    isDropShipmentOrderActive,
    canPlaceOrder,
    canAccessKitRegistration,
    canAccessDropShipmentOrder,
  })

  const handlePlaceOrderClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    onCloseMenus()
    setIsActionsMenuOpen(false)
    trackEvent('client:nav:place_order_click', { source: 'top_nav' })
    if (!isPlaceOrderActive) return
    event.preventDefault()
    onStartNew('order')
  }

  const handleKitRegistrationClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    onCloseMenus()
    setIsActionsMenuOpen(false)
    trackEvent('client:nav:register_kit_click', { source: 'top_nav' })
    if (!isKitRegistrationActive) return
    event.preventDefault()
    onStartNew('kit-registration')
  }

  const handleDropShipmentOrderClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    onCloseMenus()
    setIsActionsMenuOpen(false)
    trackEvent('client:nav:place_ds_order_click', { source: 'top_nav' })
    if (!isDropShipmentOrderActive) return
    event.preventDefault()
    if (isDropShipmentOrderCompleted) {
      onRestartDropShipmentOrder()
      return
    }
    onStartNew('drop-shipment-order')
  }

  const handleActiveActionClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    if (activeActionHref === CLIENT_KIT_REGISTRATION_FIRST_STEP_PATH) {
      handleKitRegistrationClick(event)
      return
    }
    if (activeActionHref === CLIENT_PAGES.placeDropShipmentOrder) {
      handleDropShipmentOrderClick(event)
      return
    }
    handlePlaceOrderClick(event)
  }

  return (
    <>
      {showDisabledPlaceOrder && (
        <DisabledDesktopAction
          testId="client-top-menu-place-order"
          label="Place Order"
          tooltip={PLACE_ORDER_DISABLED_TOOLTIP}
        >
          <span aria-hidden="true" className="text-lg leading-none">
            +
          </span>
          <span aria-hidden="true">Place Order</span>
        </DisabledDesktopAction>
      )}
      {showDisabledKitRegistration && (
        <DisabledDesktopAction
          testId="client-top-menu-kit-registration"
          label="Register Kit"
          tooltip={REGISTER_KIT_DISABLED_TOOLTIP}
        >
          <ClipboardPlus className="h-4 w-4" aria-hidden="true" />
          <span aria-hidden="true">Register Kit</span>
        </DisabledDesktopAction>
      )}
      {showDisabledDropShipment && (
        <DisabledDesktopAction
          testId="client-top-menu-place-ds-order"
          label="Place DS Order"
          tooltip={PLACE_DS_ORDER_DISABLED_TOOLTIP}
        >
          <CopyPlus className="h-4 w-4" aria-hidden="true" />
          <span aria-hidden="true">Place DS Order</span>
        </DisabledDesktopAction>
      )}
      {showSinglePlaceOrderAction && (
        <Link
          href={CLIENT_PAGES.placeOrder}
          className={PRIMARY_ACTION_LINK_CLASS}
          onClick={handlePlaceOrderClick}
          data-testid="client-top-menu-place-order"
        >
          <span aria-hidden="true" className="text-lg leading-none">
            +
          </span>
          <span>Place Order</span>
        </Link>
      )}
      {showSingleKitRegistrationAction && (
        <Link
          href={CLIENT_KIT_REGISTRATION_FIRST_STEP_PATH}
          className={PRIMARY_ACTION_LINK_CLASS}
          onClick={handleKitRegistrationClick}
          aria-current={isKitRegistrationActive ? 'page' : undefined}
          data-testid="client-top-menu-kit-registration"
        >
          <ClipboardPlus className="h-4 w-4" aria-hidden="true" />
          <span>Register Kit</span>
        </Link>
      )}
      {showSingleDropShipmentAction && (
        <Link
          href={CLIENT_PAGES.placeDropShipmentOrder}
          className={PRIMARY_ACTION_LINK_CLASS}
          onClick={handleDropShipmentOrderClick}
          aria-current={isDropShipmentOrderActive ? 'page' : undefined}
          data-testid="client-top-menu-place-ds-order"
        >
          <CopyPlus className="h-4 w-4" aria-hidden="true" />
          <span>Place DS Order</span>
        </Link>
      )}
      {showActionsDropdown && (
        <PopoverPrimitive.Root open={isActionsMenuOpen} onOpenChange={setIsActionsMenuOpen}>
          <div
            className={cn(
              'relative inline-flex h-10 items-stretch overflow-hidden rounded-full bg-cp-bright-blue text-sm text-white',
              'transition-colors',
              isActionsMenuOpen &&
                "after:pointer-events-none after:absolute after:inset-px after:rounded-full after:border after:border-white/70 after:content-['']",
            )}
            data-testid="client-top-menu-actions-control"
          >
            <Link
              href={activeActionHref}
              className={cn(
                'flex flex-1 items-center justify-center px-6 leading-none text-white',
                'hover:bg-cp-bright-blue-hover hover:text-white active:bg-cp-bright-blue-active active:text-white',
                'transition-colors cursor-pointer',
                FOCUS_VISIBLE_RING,
              )}
              onClick={handleActiveActionClick}
              data-testid="client-top-menu-actions-main"
            >
              {activeActionLabel}
            </Link>
            <PopoverPrimitive.Trigger asChild>
              <button
                type="button"
                className={cn(
                  'flex h-full w-12 items-center justify-center border-l border-white/20 text-white',
                  'hover:bg-cp-bright-blue-hover active:bg-cp-bright-blue-active',
                  'transition-colors cursor-pointer',
                  FOCUS_VISIBLE_RING,
                )}
                aria-label={`${activeActionLabel} actions`}
                data-testid="client-top-menu-actions-trigger"
              >
                <ChevronDown className="h-6 w-6" aria-hidden="true" />
              </button>
            </PopoverPrimitive.Trigger>
          </div>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="end"
              sideOffset={8}
              className={cn(
                'z-50 w-60 rounded-3xl border border-light-grey bg-white p-2 shadow-lg',
                'data-[state=open]:animate-in data-[state=open]:fade-in-0',
              )}
              onOpenAutoFocus={(event) => event.preventDefault()}
              data-testid="client-actions-dropdown"
            >
              <div className="px-5 pb-2 pt-1 text-sm uppercase tracking-wide text-cp-placeholder">
                Choose Action
              </div>
              {canPlaceOrder && (
                <PopoverPrimitive.Close asChild>
                  <Link
                    href={CLIENT_PAGES.placeOrder}
                    className={cn(
                      'flex w-full cursor-pointer items-center justify-between gap-3 rounded-2xl px-3 py-3 hover:bg-cp-special-grey',
                      isPlaceOrderActive && 'bg-cp-special-grey',
                    )}
                    aria-current={isPlaceOrderActive ? 'page' : undefined}
                    onClick={handlePlaceOrderClick}
                    data-testid="client-top-menu-place-order"
                  >
                    <span className="inline-flex items-center gap-3">
                      <Plus className="h-5 w-5 text-cp-bright-blue" aria-hidden="true" />
                      <span
                        className={cn(
                          'flex flex-col text-sm',
                          isPlaceOrderActive ? 'text-cp-bright-blue' : 'text-cp-dark-blue',
                        )}
                      >
                        <span>Place a New Order</span>
                        <span className="text-xs text-description-text">Order kits from Ash</span>
                      </span>
                    </span>
                    {isPlaceOrderActive && (
                      <Check className="h-4 w-4 text-cp-bright-blue" aria-hidden="true" />
                    )}
                  </Link>
                </PopoverPrimitive.Close>
              )}
              {canAccessKitRegistration && (
                <PopoverPrimitive.Close asChild>
                  <Link
                    href={CLIENT_KIT_REGISTRATION_FIRST_STEP_PATH}
                    className={cn(
                      'mt-1 flex w-full cursor-pointer items-center justify-between gap-3 rounded-2xl px-3 py-3 hover:bg-cp-special-grey',
                      isKitRegistrationActive && 'bg-cp-special-grey',
                    )}
                    aria-current={isKitRegistrationActive ? 'page' : undefined}
                    onClick={handleKitRegistrationClick}
                    data-testid="client-top-menu-kit-registration"
                  >
                    <span className="inline-flex items-center gap-3">
                      <ClipboardPlus className="h-5 w-5 text-cp-bright-blue" aria-hidden="true" />
                      <span
                        className={cn(
                          'flex flex-col text-sm',
                          isKitRegistrationActive ? 'text-cp-bright-blue' : 'text-cp-dark-blue',
                        )}
                      >
                        <span>Register Kit</span>
                        <span className="text-xs text-description-text">
                          Log a kit you already have
                        </span>
                      </span>
                    </span>
                    {isKitRegistrationActive && (
                      <Check className="h-4 w-4 text-cp-bright-blue" aria-hidden="true" />
                    )}
                  </Link>
                </PopoverPrimitive.Close>
              )}
              {canAccessDropShipmentOrder && (
                <PopoverPrimitive.Close asChild>
                  <Link
                    href={CLIENT_PAGES.placeDropShipmentOrder}
                    className={cn(
                      'mt-1 flex w-full cursor-pointer items-center justify-between gap-3 rounded-2xl px-3 py-3 hover:bg-cp-special-grey',
                      isDropShipmentOrderActive && 'bg-cp-special-grey',
                    )}
                    aria-current={isDropShipmentOrderActive ? 'page' : undefined}
                    onClick={handleDropShipmentOrderClick}
                    data-testid="client-top-menu-place-ds-order"
                  >
                    <span className="inline-flex items-center gap-3">
                      <CopyPlus className="h-5 w-5 text-cp-bright-blue" aria-hidden="true" />
                      <span
                        className={cn(
                          'flex flex-col text-sm',
                          isDropShipmentOrderActive ? 'text-cp-bright-blue' : 'text-cp-dark-blue',
                        )}
                      >
                        <span>Place a DS Order</span>
                        <span className="text-xs text-description-text">
                          Create drop shipment request
                        </span>
                      </span>
                    </span>
                    {isDropShipmentOrderActive && (
                      <Check className="h-4 w-4 text-cp-bright-blue" aria-hidden="true" />
                    )}
                  </Link>
                </PopoverPrimitive.Close>
              )}
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      )}
    </>
  )
}

export default function NavMenuDesktop({
  items,
  pathname,
  canPlaceOrder,
  canAccessKitRegistration,
  canAccessDropShipmentOrder,
  canViewFiles = false,
  isProfileMenuOpen,
  onProfileMenuOpenChange,
  isProgramScopedNavDisabled = false,
  hasSelectedClient = true,
  isImpersonating = false,
}: Readonly<NavMenuDesktopProps>) {
  const router = useRouter()
  const { isSuperAdmin, isSuperAdminCheckReady } = useIsSuperAdmin()
  const isProfileSectionActive =
    isPathActive(pathname, CLIENT_PAGES.myProfile) ||
    isPathActive(pathname, CLIENT_PAGES.users) ||
    isPathActive(pathname, CLIENT_PAGES.files) ||
    isPathActive(pathname, CLIENT_PAGES.theme) ||
    isPathActive(pathname, CLIENT_PAGES.eligibilityQuestionnaireBuilder)
  const isMyProfileActive = isPathActive(pathname, CLIENT_PAGES.myProfile)
  const isUsersActive = isPathActive(pathname, CLIENT_PAGES.users)
  const isFilesActive = isPathActive(pathname, CLIENT_PAGES.files)
  const isThemeActive = isPathActive(pathname, CLIENT_PAGES.theme)
  const isEligibilityQuestionnaireBuilderActive = isPathActive(
    pathname,
    CLIENT_PAGES.eligibilityQuestionnaireBuilder,
  )
  const [startNewModalType, setStartNewModalType] = useState<StartNewModalVariant | null>(null)
  const [isDropShipmentOrderCompleted, setIsDropShipmentOrderCompleted] = useState(false)
  const canViewThemeSettings = isSuperAdminCheckReady && isSuperAdmin
  const canViewEligibilityQuestionnaireBuilder = isSuperAdminCheckReady && isSuperAdmin
  const closeProfileMenu = () => onProfileMenuOpenChange(false)
  const closeStartNewModal = () => setStartNewModalType(null)

  const restartDropShipmentOrder = useCallback(() => {
    globalThis.dispatchEvent(new Event(START_NEW_DROP_SHIPMENT_ORDER_EVENT))
    router.replace(CLIENT_PAGES.placeDropShipmentOrder)
  }, [router])

  useEffect(() => {
    const handleDropShipmentOrderState = (event: Event) => {
      const detail = (event as CustomEvent<DropShipmentOrderStateDetail>).detail
      setIsDropShipmentOrderCompleted(detail?.isCompleted ?? false)
    }

    globalThis.addEventListener(DROP_SHIPMENT_ORDER_STATE_EVENT, handleDropShipmentOrderState)
    return () => {
      globalThis.removeEventListener(DROP_SHIPMENT_ORDER_STATE_EVENT, handleDropShipmentOrderState)
    }
  }, [])

  const handleConfirmStartNew = () => {
    const variant = startNewModalType
    closeStartNewModal()
    if (variant === 'drop-shipment-order') {
      restartDropShipmentOrder()
      return
    }
    if (variant === 'kit-registration') {
      globalThis.dispatchEvent(new Event(START_NEW_KIT_REGISTRATION_EVENT))
      router.replace(CLIENT_KIT_REGISTRATION_FIRST_STEP_PATH)
      return
    }
    globalThis.dispatchEvent(new Event(START_NEW_ORDER_EVENT))
    router.replace(CLIENT_PAGES.placeOrder)
  }

  return (
    <div className="flex w-full sm:w-auto sm:ml-auto">
      <nav
        className="flex w-full items-center justify-end gap-3 sm:gap-4"
        aria-label="Client top menu actions"
        data-testid="client-top-menu-actions"
      >
        {items.map(({ id, label, href }) => {
          const disableOrders = Boolean(isProgramScopedNavDisabled && id === 'orders')
          const isActive =
            !disableOrders &&
            (id === 'dashboard' ? isDashboardNavPath(pathname) : isPathActive(pathname, href))
          return (
            <NavLink
              key={id}
              href={href}
              label={label}
              isActive={isActive}
              testId={`client-top-menu-${id}`}
              onClick={closeProfileMenu}
              disabled={disableOrders}
              disabledTooltip={disableOrders ? ORDERS_DISABLED_TOOLTIP : undefined}
              className={isImpersonating ? 'text-cp-dark-blue' : undefined}
            />
          )
        })}
        <span
          className="h-4 w-px bg-cp-special-grey/10"
          aria-hidden="true"
          data-testid="client-top-menu-separator"
        />
        <DesktopPrimaryActions
          pathname={pathname}
          canPlaceOrder={canPlaceOrder}
          canAccessKitRegistration={canAccessKitRegistration}
          canAccessDropShipmentOrder={canAccessDropShipmentOrder}
          isProgramScopedNavDisabled={isProgramScopedNavDisabled}
          isDropShipmentOrderCompleted={isDropShipmentOrderCompleted}
          onCloseMenus={closeProfileMenu}
          onStartNew={setStartNewModalType}
          onRestartDropShipmentOrder={restartDropShipmentOrder}
        />
        <PopoverPrimitive.Root open={isProfileMenuOpen} onOpenChange={onProfileMenuOpenChange}>
          <PopoverPrimitive.Trigger asChild>
            <button
              type="button"
              aria-label="User profile"
              className={cn(
                'relative inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-cp-special-grey/10',
                'hover:bg-cp-special-grey/20',
                isProfileSectionActive &&
                  "after:pointer-events-none after:absolute after:inset-px after:rounded-full after:border after:border-cp-placeholder after:content-['']",
              )}
              data-testid="client-top-menu-profile"
            >
              <UserIcon
                size={22}
                fill={
                  isProfileSectionActive ? (isImpersonating ? 'currentColor' : 'white') : 'none'
                }
                strokeWidth={isProfileSectionActive ? 0.5 : 1.5}
                stroke="currentColor"
                className={isImpersonating ? 'text-cp-dark-blue' : 'text-white'}
                aria-hidden="true"
              />
            </button>
          </PopoverPrimitive.Trigger>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="end"
              sideOffset={8}
              className={cn(
                'z-50 w-56 rounded-3xl border border-light-grey bg-white p-2 shadow-lg',
                'data-[state=open]:animate-in data-[state=open]:fade-in-0',
              )}
              onOpenAutoFocus={(event) => event.preventDefault()}
              data-testid="client-profile-dropdown"
            >
              <DesktopProfileItem
                href={CLIENT_PAGES.myProfile}
                label="My Profile"
                Icon={UserRound}
                isActive={isMyProfileActive}
                disabled={!hasSelectedClient}
                onClick={closeProfileMenu}
                testId="client-profile-dropdown-my-profile"
              />
              <DesktopProfileItem
                href={CLIENT_PAGES.users}
                label="Users"
                Icon={Users}
                isActive={isUsersActive}
                disabled={!hasSelectedClient}
                onClick={closeProfileMenu}
                testId="client-profile-dropdown-users"
                className="mt-1"
              />
              {canViewFiles && (
                <DesktopProfileItem
                  href={CLIENT_PAGES.files}
                  label="Files"
                  Icon={FileText}
                  isActive={isFilesActive}
                  disabled={isProgramScopedNavDisabled}
                  onClick={closeProfileMenu}
                  testId="client-profile-dropdown-files"
                  className="mt-1"
                />
              )}
              {canViewThemeSettings && (
                <DesktopProfileItem
                  href={CLIENT_PAGES.theme}
                  label="Theme"
                  Icon={Palette}
                  isActive={isThemeActive}
                  disabled={isProgramScopedNavDisabled}
                  onClick={closeProfileMenu}
                  testId="client-profile-dropdown-theme"
                  className="mt-1"
                />
              )}
              {canViewEligibilityQuestionnaireBuilder && (
                <DesktopProfileItem
                  href={CLIENT_PAGES.eligibilityQuestionnaireBuilder}
                  label="EQ Builder"
                  Icon={ClipboardList}
                  isActive={isEligibilityQuestionnaireBuilderActive}
                  disabled={isProgramScopedNavDisabled}
                  onClick={closeProfileMenu}
                  testId="client-profile-dropdown-eq-builder"
                  className="mt-1"
                />
              )}
              <Link
                href="/logout?reason=logout"
                className="mt-1 flex w-full cursor-pointer items-center gap-3 rounded-full px-3 py-3 hover:bg-cp-special-grey border-t border-light-grey/60 pt-3"
                onClick={closeProfileMenu}
                data-testid="client-profile-dropdown-logout"
              >
                <SignOutIcon
                  className="h-5 w-5 text-cp-coral"
                  aria-hidden="true"
                  data-testid="client-profile-dropdown-logout-icon"
                />
                <span className="text-sm text-cp-dark-blue">Log out</span>
              </Link>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      </nav>
      <StartNewModal
        variant={startNewModalType}
        isSubmitting={false}
        onOpenChange={(open) => {
          if (!open) closeStartNewModal()
        }}
        onCancel={closeStartNewModal}
        onConfirm={handleConfirmStartNew}
      />
    </div>
  )
}
